/* 🔴 F15 STEP 4.1a — HIS RULE TABS, AND THE CARD IS THE EDITOR (DECISIONS 2026-10-31 evening "4.1a: THE CARD IS THE EDITOR",
   his "Yes, add Now" the same night; the shape is `docs/F15-COACH-ENGINE-DESIGN.md` §9 with 4.1a′'s per-set table). Under a
   main row's title: `Now · Step 1 · Step 2 · +`. Now is the row as written and is never a stored step. Inside a step, `Target ·
   Action`: the card's OWN set table, tempo, each side and note show and edit the chosen half — no boxes of the editor's own.
   This file is the model (what each half shows, what an edit writes); the view is `components/RuleTabs.tsx`.

   ONE VOCABULARY. The editor never states a step's JSON for the database itself: it holds each step as raw §9 JSON while the
   coach edits, and WRITES only through the server's own reader and writer — `tabsJson(raw.map(readStep))` — and only once every
   step reads. So what is written is exactly what both runtimes read (pinned: the corpus's `editor:` shapes, read by
   `CoachTabs.swift` and `coachTabs.ts`), and a step nobody touched is written back byte for byte (`stepJson ∘ readStep`).

   EACH HALF OPENS PREFILLED FROM THE TAB BEFORE IT: Step 1 from Now, Step k from the row as Step k−1 leaves it (`after`). A new
   step is a DRAFT — held here, never written — until its Action changes something; a target is the card's WORKING rows (reps ·
   kg · seconds — never rest, never a warm-up), an action the card's WHOLE set table, ramp rows included.

   THE LIVE TAB IS DERIVED, never stored (§9): a template has no client and no log, so no tab is marked; a client copy's
   position is still open (QUEUE F15: the live tab marked on a client copy). */
import {
  type SetType as TabSetType, type TabSet, type TabStep, applyChange, changeWords, migrate, readStep, tabSteps, tabsJson,
  targetWords,
} from "../../../supabase/functions/_shared/coachTabs";
import type { SetRow } from "../../../supabase/functions/_shared/progression";
import { type ExerciseDraft, type SetDraft, type TrackingField, emptySet } from "./builder";
import { rowRuleShort } from "./workoutEngine";

/** His word: "as many tabs we want or limit them to as much can fit in that container" — six steps and the + fit the card's
 *  head at 1024 (MEASURED on the stills); the + goes at six. */
export const MAX_STEPS = 6;
/** §9's table bound: 1–20 rows. */
export const MAX_ROWS = 20;

/** The tab strip's first tab — the row as written. */
export const NOW_LABEL = "Now";
export const stepLabel = (i: number): string => `Step ${String(i + 1)}`;
export const PAUSE = "Pause";
export const RESUME = "Resume";

export type Raw = Record<string, unknown>;
export type Part = "target" | "action";

/** The row as one step leaves it — what the next step's halves open on. `hidden` = paused (a deactivated row). */
export type RowState = {
  exerciseId: string; sets: SetDraft[]; tempo: string | null; eachSide: boolean; note: string | null; hidden: boolean;
};

const isObj = (v: unknown): v is Raw => v !== null && typeof v === "object" && !Array.isArray(v);
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

export function nowState(ex: ExerciseDraft): RowState {
  return { exerciseId: ex.libraryExerciseId.toLowerCase(), sets: ex.sets, tempo: ex.tempo, eachSide: ex.eachSide,
           note: ex.note, hidden: false };
}

/* ── the two set vocabularies: the card's `SetDraft`, §9's table row / the planner's `SetRow` ──────────────────────────── */

const blank = (n: number | null, floor: number): number | null => (n === null || n < floor ? null : n);

/** A card row as §9's action row: a blank cell an absent key, 0 kg a blank weight, 0 reps / 0 s blank (the reader's floor). */
export function tableRow(s: SetDraft): TabSet {
  return { type: s.type as TabSetType, reps: blank(s.reps, 1), kg: s.weightKg === null || s.weightKg <= 0 ? null : s.weightKg,
           seconds: blank(s.timeSeconds, 1), rest: blank(s.restSeconds, 0) };
}

function draftOfRow(r: { type?: unknown; reps?: unknown; kg?: unknown; seconds?: unknown; rest?: unknown }): SetDraft {
  const type = typeof r.type === "string" && ["regular", "warm_up", "drop_set", "failure"].includes(r.type)
    ? (r.type as SetDraft["type"]) : "regular";
  return { ...emptySet(), type, reps: num(r.reps), weightKg: num(r.kg), timeSeconds: num(r.seconds), restSeconds: num(r.rest) };
}

const asSetRows = (sets: readonly SetDraft[]): SetRow[] => sets.map((s, i) => ({
  position: i, reps: s.reps, weightKg: s.weightKg, timeSeconds: s.timeSeconds, restSeconds: s.restSeconds, setType: s.type,
}));
const fromSetRows = (rows: readonly SetRow[]): SetDraft[] => rows.map((r) => ({
  ...emptySet(), type: r.setType as SetDraft["type"], reps: r.reps, weightKg: r.weightKg, timeSeconds: r.timeSeconds,
  restSeconds: r.restSeconds,
}));

/** The row after a step that READ — `applyChange` (the planner's own) for the sets; the row fields only where it names them. */
export function after(step: TabStep, before: RowState): RowState {
  const a = step.action;
  if (a.kind === "set") {
    const c = a.change;
    return {
      ...before,
      sets: fromSetRows(applyChange(c, asSetRows(before.sets))),
      tempo: c.tempo ?? before.tempo,
      eachSide: c.eachSide ?? before.eachSide,
      note: c.note === null ? before.note : c.note === "" ? null : c.note,
    };
  }
  if (a.kind === "next") return { ...before, exerciseId: a.next };
  if (a.kind === "deactivate") return { ...before, hidden: true };
  if (a.kind === "activate") return { ...before, hidden: false };
  return before;
}

/** The row BEFORE each step (index i = what Step i+1 opens on); a step that does not read yet leaves the row as it was. */
export function befores(raws: readonly Raw[], now: RowState): RowState[] {
  const out: RowState[] = [];
  let at = now;
  for (const raw of raws) {
    out.push(at);
    const step = readStep(raw);
    if (step !== null) at = after(step, at);
  }
  return out;
}

/** True when the step moves the row at all — a draft is written only then. */
export function changes(step: TabStep, before: RowState): boolean {
  const a = step.action;
  if (a.kind === "next") return a.next !== before.exerciseId;
  if (a.kind === "deactivate") return !before.hidden;
  if (a.kind === "activate") return before.hidden;
  if (a.kind === "rule") return true;
  const next = after(step, before);
  return JSON.stringify(next.sets.map(tableRow)) !== JSON.stringify(before.sets.map(tableRow))
    || next.tempo !== before.tempo || next.eachSide !== before.eachSide || next.note !== before.note;
}

/* ── the stored list, and a new step ──────────────────────────────────────────────────────────────────────────────── */

/** The steps a row's effective config holds as raw JSON, every old kind MIGRATED (§9) — a preset reads as one standing tab, a
 *  ladder as `next` steps. `brokenAt`: the first step that does not read (the list ends there, said). */
export function storedRaws(config: unknown): { raws: Raw[]; brokenAt: number | null } {
  const parsed = tabSteps(migrate(config));
  if (parsed === null) return { raws: [], brokenAt: null };
  return { raws: tabsJson(parsed.steps).steps, brokenAt: parsed.brokenAt };
}

/** The target half's units: what the row tracks, of the three a target can name (§9: rounds and distance are not targets). */
export const TARGET_FIELDS: readonly TrackingField[] = ["reps", "weight", "time"];
/** A row the tabs can describe: nothing tracked beyond reps · weight · time (rounds/distance rows are frozen whole today). */
export const tabsFit = (tracking: readonly TrackingField[]): boolean => tracking.every((t) => TARGET_FIELDS.includes(t));

function targetEntry(s: SetDraft, tracking: readonly TrackingField[]): Raw {
  const o: Raw = {};
  const any = tracking.length === 0;
  if ((any || tracking.includes("reps")) && blank(s.reps, 1) !== null) o.reps = s.reps;
  if (tracking.includes("weight") && s.weightKg !== null && s.weightKg > 0) o.kg = s.weightKg;
  if (tracking.includes("time") && blank(s.timeSeconds, 1) !== null) o.seconds = s.timeSeconds;
  return o;
}

/** A new step, prefilled from the row as the tab before leaves it: the target = its working rows (reach what is written),
 *  the action = its whole set table (change nothing yet). After a Pause the row is hidden and never logged, so the only
 *  target it can meet is weeks — blank until typed — and the only action is Resume (§9). */
export function newStep(before: RowState, tracking: readonly TrackingField[]): Raw {
  if (before.hidden) return { target: {}, action: { activate: true } };
  const working = before.sets.filter((s) => s.type !== "warm_up");
  return {
    target: { table: working.map((s) => targetEntry(s, tracking)), sessions: 1 },
    action: { set: { table: before.sets.map(tableRow).map(rowJson) } },
  };
}

function rowJson(r: TabSet): Raw {
  const o: Raw = {};
  if (r.type !== "regular") o.type = r.type;
  if (r.reps !== null) o.reps = r.reps;
  if (r.kg !== null) o.kg = r.kg;
  if (r.seconds !== null) o.seconds = r.seconds;
  if (r.rest !== null) o.rest = r.rest;
  return o;
}

/* ── what each half SHOWS on the card ──────────────────────────────────────────────────────────────────────────────── */

const targetOf = (raw: Raw): Raw => (isObj(raw.target) ? raw.target : {});
const actionOf = (raw: Raw): Raw => (isObj(raw.action) ? raw.action : {});

export type TargetView =
  | { kind: "sets"; sets: SetDraft[]; twice: boolean; note: string | null }
  | { kind: "weeks"; weeks: number | null; note: string | null }
  | { kind: "every" };

/** The target half as the card draws it — read leniently, so a half the coach is still typing (a blank row) still shows. A
 *  uniform target (`{reps: 8}` — written before 4.1a′) shows as that number on each working set. */
export function targetView(raw: Raw, before: RowState): TargetView {
  const t = targetOf(raw);
  const note = typeof t.note === "string" ? t.note : null;
  if (t.every !== undefined && t.every !== null) return { kind: "every" };
  if (actionOf(raw).activate === true || (t.weeks !== undefined && t.weeks !== null)) return { kind: "weeks", weeks: num(t.weeks), note };
  const twice = t.sessions === 2;
  if (Array.isArray(t.table)) return { kind: "sets", sets: t.table.map((e) => draftOfRow(isObj(e) ? e : {})), twice, note };
  const working = before.sets.filter((s) => s.type !== "warm_up");
  const one = draftOfRow({ reps: t.reps, kg: t.kg, seconds: t.seconds });
  return { kind: "sets", sets: (working.length === 0 ? [one] : working).map(() => ({ ...one })), twice, note };
}

export type ActionView =
  | { kind: "set"; sets: SetDraft[]; tempo: string | null; eachSide: boolean; note: string | null }
  | { kind: "next"; next: string }
  | { kind: "deactivate" }
  | { kind: "activate" }
  | { kind: "rule"; rule: Raw };

/** The action half as the card draws it: the set table it writes (a uniform change applied to the row before it, the
 *  planner's own `applyChange`), and tempo · each side · note — each the step's own where it names one, else the row's. */
export function actionView(raw: Raw, before: RowState): ActionView {
  const a = actionOf(raw);
  if (typeof a.next === "string") return { kind: "next", next: a.next.toLowerCase() };
  if (a.deactivate === true) return { kind: "deactivate" };
  if (a.activate === true) return { kind: "activate" };
  if (isObj(a.rule)) return { kind: "rule", rule: a.rule };
  const s = isObj(a.set) ? a.set : {};
  const step = readStep(raw);
  const sets = Array.isArray(s.table) ? s.table.map((e) => draftOfRow(isObj(e) ? e : {}))
    : step !== null ? after(step, before).sets : before.sets;
  return {
    kind: "set", sets,
    tempo: typeof s.tempo === "string" ? s.tempo : before.tempo,
    eachSide: typeof s.each_side === "boolean" ? s.each_side : before.eachSide,
    note: typeof s.note === "string" ? (s.note === "" ? null : s.note) : before.note,
  };
}

/* ── what an EDIT on the card writes into the step ─────────────────────────────────────────────────────────────────── */

/** The card's working rows as the target table (reps · kg · seconds the row tracks; nothing else); the chip and the note kept. */
export function withTargetSets(raw: Raw, sets: readonly SetDraft[], tracking: readonly TrackingField[], note: string | null): Raw {
  const t = targetOf(raw);
  const target: Raw = { table: sets.map((s) => targetEntry(s, tracking)), sessions: t.sessions === 2 ? 2 : 1 };
  if (note !== null && note !== "") target.note = note;
  return { ...raw, target };
}

export function withTwice(raw: Raw, twice: boolean): Raw {
  return { ...raw, target: { ...targetOf(raw), sessions: twice ? 2 : 1 } };
}

export function withWeeks(raw: Raw, weeks: number | null, note: string | null): Raw {
  const target: Raw = weeks === null ? {} : { weeks };
  if (note !== null && note !== "") target.note = note;
  return { ...raw, target };
}

/** The card's WHOLE set table as the action, ramp rows included; tempo · note · each side written only where they differ
 *  from the row before (a note cleared is `""` — "no note next week"; a tempo cannot be cleared by a step, §9's reader). */
export function withActionSets(raw: Raw, view: { sets: readonly SetDraft[]; tempo: string | null; eachSide: boolean; note: string | null },
                               before: RowState): Raw {
  const set: Raw = { table: view.sets.map(tableRow).map(rowJson) };
  if (view.tempo !== null && view.tempo !== before.tempo) set.tempo = view.tempo;
  if (view.note !== before.note) set.note = view.note ?? "";
  if (view.eachSide !== before.eachSide) set.each_side = view.eachSide;
  return { ...raw, action: { set } };
}

/** The title's pick in Action: another exercise = `next`; the row's own exercise back = its set table as it was. */
export function withExercise(raw: Raw, id: string, before: RowState): Raw {
  return id.toLowerCase() === before.exerciseId ? withActionSets(raw, before, before) : { ...raw, action: { next: id.toLowerCase() } };
}

/** Action's "Pause" chip: on = deactivate; off = the set table as it was. */
export function withPause(raw: Raw, on: boolean, before: RowState): Raw {
  return on ? { ...raw, action: { deactivate: true } } : withActionSets(raw, before, before);
}

/* ── when a list is written, and the words when it is not ─────────────────────────────────────────────────────────── */

/** Why a step cannot be written yet — the card's caption; null when it reads. */
export function notYet(raw: Raw): string | null {
  if (readStep(raw) !== null) return null;
  const t = targetOf(raw);
  const a = actionOf(raw);
  if (a.activate === true && (t.weeks === undefined || t.weeks === null)) return "Not saved — how many weeks?";
  if (Array.isArray(t.table) && (t.table.length === 0 || t.table.length > MAX_ROWS)) return `Not saved — 1 to ${String(MAX_ROWS)} target sets`;
  if (Array.isArray(t.table) && t.table.some((e) => !isObj(e) || Object.keys(e).length === 0)) return "Not saved — a target set is blank";
  const s = isObj(a.set) ? a.set : null;
  if (s !== null && Array.isArray(s.table)) {
    if (s.table.length === 0 || s.table.length > MAX_ROWS) return `Not saved — 1 to ${String(MAX_ROWS)} sets`;
    if (s.table.every((e) => isObj(e) && e.type === "warm_up")) return "Not saved — needs a working set";
  }
  return "Not saved — this step can't be read";
}

export const DRAFT_SAYS = "Not saved — change something in Action";

/** EXACTLY what the editor writes to `coach_workout_rules.progression_config`: the server's own reader over every raw step,
 *  then its own writer; null while any step does not read (nothing half-written is ever written). */
export function writeOf(raws: readonly Raw[]): { rule: string; steps: Raw[] } | null {
  const steps = raws.map(readStep);
  return steps.every((s): s is TabStep => s !== null) ? tabsJson(steps) : null;
}

/** A standing tab (a §5 preset run unchanged) ends the list: nothing after it is ever reached, so nothing is added after it. */
export const isStanding = (raw: Raw | undefined): boolean => raw !== undefined && isObj(raw.target) && typeof raw.target.every === "string";

/** One step in words, for the tab's hover and its accessible name: "At 2 × 5 reps @ 30 kg twice running → 4 × 4 @ 32.5 kg",
 *  "After 2 weeks → resumed"; a standing tab as its preset reads today. */
export function stepWords(raw: Raw, names: ReadonlyMap<string, string>): string {
  const step = readStep(raw);
  if (step === null) return notYet(raw) ?? "";
  const a = step.action;
  if (a.kind === "rule") return rowRuleShort({ config: a.rule, source: "workout" }).text;
  const to = a.kind === "set" ? changeWords(a.change) : a.kind === "next" ? (names.get(a.next) ?? "a deleted exercise")
    : a.kind === "deactivate" ? "paused" : "resumed";
  return `${step.target.kind === "weeks" ? "After" : "At"} ${targetWords(step.target)} → ${to}`;
}

